import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Select, MenuItem,
  FormControl, InputLabel, Button, Badge, IconButton, Alert
} from '@mui/material';
import { 
  Refresh, ArrowUpward, ArrowDownward, NotInterested,
  Notifications, CheckCircle, LocalShipping, DoneAll 
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const statusColors = {
  canceled: 'error',
  pending: 'warning',
  processing: 'info',
  shipped: 'primary',
  delivered: 'success'
};

const statusTxt = {
  canceled: 'ملغي',
  pending: 'معلق',
  processing: 'قيد التحضير',
  shipped: 'قيد التوصيل',
  delivered: 'مكتمل'
};

const statusIcons = {
  pending: <Notifications color="warning" />,
  processing: <CheckCircle color="info" />,
  shipped: <LocalShipping color="primary" />,
  delivered: <DoneAll color="success" />,
  canceled: <NotInterested color="error" />
};

const AdminOrdersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortNewest, setSortNewest] = useState(true);
  const [stats, setStats] = useState(null);
  const [pendingAlert, setPendingAlert] = useState(0);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/orders?status=${filter !== 'all' ? filter : ''}&newest=${sortNewest}`);
      setOrders(response.data);
      setError(null);
      
      // Get stats for the first load
      if (!stats) {
        const statsRes = await api.get('/admin/orders/stats');
        setStats(statsRes.data);
        setPendingAlert(statsRes.data.pendingCount);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load orders');
      console.error('Orders fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      fetchOrders(); // Refresh data
    } catch (err) {
      console.error('Status update error:', err);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchOrders();
      
      // Set up periodic refresh (every 2 minutes)
      const interval = setInterval(fetchOrders, 120000);
      return () => clearInterval(interval);
    }
  }, [user, filter, sortNewest]);

  if (user?.role !== 'admin') {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Admin access required</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 , mb: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">إدارة الطلبات</Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<Refresh />}
            onClick={fetchOrders}
            disabled={loading}
          >
            تحديث
          </Button>
          
          <Badge badgeContent={pendingAlert} color="error" overlap="circular">
            <Chip 
              icon={<Notifications />}
              label="طلبات معلقة"
              color="warning"
              variant={filter === 'pending' ? 'filled' : 'outlined'}
              onClick={() => setFilter('pending')}
              clickable
            />
          </Badge>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        <FormControl sx={{ minWidth: 120 }} size="small">
          <InputLabel>فلترة</InputLabel>
          <Select
            value={filter}
            label="فلترة"
            onChange={(e) => setFilter(e.target.value)}
          >
            <MenuItem value="all">الكل</MenuItem>
            <MenuItem value="pending">معلقة</MenuItem>
            <MenuItem value="processing">قيد التحضير</MenuItem>
            <MenuItem value="shipped">قيد التوصيل</MenuItem>
            <MenuItem value="delivered">مكتملة</MenuItem>
            <MenuItem value="canceled">ملغية</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          endIcon={sortNewest ? <ArrowDownward /> : <ArrowUpward />}
          onClick={() => setSortNewest(!sortNewest)}
        >
          ترتيب حسب: {sortNewest ? 'الأحدث أولا' : 'الأقدم أولا'}
        </Button>

        {stats && (
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            {stats.stats.map((stat) => (
              <Chip
                key={stat.status}
                icon={statusIcons[stat.status]}
                label={`${statusTxt[stat.status]}: ${stat.count}`}
                color={statusColors[stat.status]}
                variant={filter === stat.status ? 'filled' : 'outlined'}
                onClick={() => setFilter(stat.status)}
                clickable
              />
            ))}
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>رقم الطلب</TableCell>
              <TableCell>العميل</TableCell>
              <TableCell>المنتجات</TableCell>
              <TableCell>الإجمالي</TableCell>
              <TableCell>التاريخ</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>الإجراءات</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  جار تحميل الطلبات ...
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  لا يوجد طلبات جديدة
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>#{order.id}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography>{order.user.username}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {order.user.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {order.items.slice(0, 2).map((item) => (
                      <Typography key={item.id}>
                        {item.quantity}x {item.product.name}
                      </Typography>
                    ))}
                    {order.items.length > 2 && (
                      <Typography color="text.secondary">
                        +{order.items.length - 2} more
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>E.G.P {order.total_amount}</TableCell>
                  <TableCell>
                    {new Date(order.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusTxt[order.status]}
                      color={statusColors[order.status]}
                      icon={statusIcons[order.status]}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                      disabled = {order.status === 'delivered'}
                        value={order.status}
                        onChange={(e) => updateStatus(order.id, e.target.value)}
                      >
                        <MenuItem value="pending">معلقة</MenuItem>
                        <MenuItem value="processing">قيد التحضير</MenuItem>
                        <MenuItem value="shipped">قيد التوصيل</MenuItem>
                        <MenuItem value="delivered">مكتملة</MenuItem>
                        <MenuItem value="canceled">ملغية</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                   <TableCell>
                       <IconButton
                          aria-label="view"
                          color="primary"
                          onClick={() => navigate(`/admin/orders/${order.id}`)}
                        >
                           <ArrowBackIcon />
                       </IconButton>
                    </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AdminOrdersPage;